import { select, call, takeLatest, put } from 'redux-saga/effects'
import Logger from '../../../services/logger.service'

import { getServiceIdSelector } from '../../user/selectors'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'

import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { FillablePDFsConstants } from '../store/fillablePDFs.constants'
import { setFillablePDFsImportError, onFillablePDFsImportSuccess } from '../store/fillablePDFs.actions'

import { ClientDataService, IClientDataDoc, clientData } from '../../../services/clientData.service'
import {
  FillablePdfsImportService,
  IFillablePdfsFetchRequest,
  IFillablePdfsFetchResponse,
} from '../services/fillablePdfsImport.service'
import { safeJsonParse } from '../../../utilities/safeParse.util'
import { buildClientDataRequestForType, ClientDataContentType } from '../../../utilities/clientData.util'

export function* setFillablePdfData() {
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)

  try {
    const serviceId: string = yield select(getServiceIdSelector)

    const importService = new FillablePdfsImportService(authenticationJwt as string)

    const localService = new ClientDataService(serviceId)

    const allLocalDocs: clientData = yield call([localService, localService.getAllDoc])
    const clientDataReq = buildClientDataRequestForType(allLocalDocs, ClientDataContentType.PDF)

    const request: IFillablePdfsFetchRequest = {
      includePdfData: true,
      includeHidden: false,
      supplyOmittedDocuments: true,
      clientData: clientDataReq,
    }

    const response: IFillablePdfsFetchResponse = yield call([importService, importService.fetchFillablePdfs], request)

    if (response.status === 200) {
      const serverDocs: any = response.data?.clientData ?? []

      if (serverDocs && serverDocs.length) {
        for (const serverDoc of serverDocs) {
          const parsed = safeJsonParse(serverDoc.docData)
          if (!parsed) {
            Logger.error(`Error parsing serverDoc in setFillablePdfData: ${serverDoc.id}`)
            continue
          }

          const doc: IClientDataDoc = {
            id: parsed.id ?? serverDoc.id,
            contentType: serverDoc.contentType,
            contentDataType: parsed.contentDataType ?? serverDoc.contentDataType,
            createDateTime: parsed.createDateTime ?? serverDoc.createDateTime,
            entity: parsed.entity ?? null,
          }

          yield call([localService, localService.createOrUpdateDocument], doc)
        }
      }
      yield put(setFillablePDFsImportError(''))
      yield put(onFillablePDFsImportSuccess())
      Logger.info(`FillablePDFs: setFillablePdfData success, docs count: ${serverDocs?.length ?? 0}`)
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index]?.message
      } else {
        response.responseMessage =
          'There was an error retrieving fillable PDFs. Please contact customer support for further assistance.'
      }
      yield put(handleApiError(response))
    }
  } catch (e: any) {
    let cause = ''
    if (e.messages && e.messages.length) {
      const index = e.messages.findIndex((x: any) => x.status === e.status)
      cause = e.messages[index]?.message || ''
      yield put(handleApiError(e))
    } else {
      cause = e.message ? e.message : 'unknown'
      const errorResponse = {
        status: null,
        messages: [
          {
            status: null,
            display: [
              { id: 3, name: 'Alert' },
              { id: 1, name: 'Log' },
            ],
            message: `An error occurred during fillable PDFs retrieval. Cause: ${cause}`,
          },
        ],
      }
      yield put(handleApiError(errorResponse))
    }
    yield put(setFillablePDFsImportError(cause || e.toString()))
    Logger.error(`Error retrieving fillable pdfs, ${e}`)
  }
}

export function* setFillablePdfDataSaga() {
  yield takeLatest([FillablePDFsConstants.SET_FILLABLE_PDF_DATA], setFillablePdfData)
}
